import React, {useEffect, useRef} from "react";
import styles from "./OfferTable.module.css";
import {OfferTableHeader} from "./header/OfferTableHeader";
import {useAppDispatch, useAppSelector} from "../../../../app/hooks";
import {TablePagination} from "../../../elements/pagination/TablePagination";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {
    defaultOfferTableRequestRequest,
    fetchOfferList,
    fetchOfferMakeOptions,
    fetchOfferModelOptions,
    fetchOfferSourceNameOptions,
    offerListRequestStatusSelector,
    offerListSelector,
    offerListTotalCountSelector,
    OFFERS_TABLE_ITEMS_ON_PAGE,
    offerTableRequestSelector,
    updateOfferTableRequest,
} from "../offerListSlice";
import {OfferTableRow} from "./row/OfferTableRow";
import {TableLoader} from "../../../elements/tableLoader/TableLoader";
import {OfferTableControls} from "./controls/OfferTableControls";
import {showErrorToastNotification} from "../../../elements/toastNotification/toastNotificationSlice";
import {Button, ButtonState} from "../../../elements/button/Button";
import {FormName, openForm} from "../../../elements/formProvider/formSlice";
import {openPopupModal, PopupModalName} from "../../../elements/popupModalProvider/popupModalSlice";
import {
    DISCOUNT_EXAMPLES,
    DISCOUNT_HEADERS,
    FINANCE_EXAMPLES,
    FINANCE_HEADERS,
    parseCsv,
    REQUIRED_EXAMPLES,
    REQUIRED_HEADERS,
    validateAndMapRows,
    validateHeaders
} from "./util/offerCsvUtils";

export enum OfferLevel {
    ASSISTANT = "ASSISTANT",
    LOCATION = "LOCATION"
}

interface OfferTableProps {
    publicBotId?: string;
    publicLocationId?: string;
    offerLevel: OfferLevel;
}

export const OfferTable: React.FC<OfferTableProps> = ({
                                                          publicBotId,
                                                          publicLocationId,
                                                          offerLevel
                                                      }) => {

    const dispatch = useAppDispatch()
    const offerList = useAppSelector(offerListSelector)
    const offerListTotalCount = useAppSelector(offerListTotalCountSelector)
    const offerTableRequest = useAppSelector(offerTableRequestSelector)
    const offerListRequestStatus = useAppSelector(offerListRequestStatusSelector)
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const isFiltersApplied = offerTableRequest !== defaultOfferTableRequestRequest

    const {
        pageNumber,
        searchQuery,
        makeList,
        modelList,
        offerTypeList,
        offerSourceNameList,
    } = offerTableRequest

    useEffect(() => {
        if (offerLevel === OfferLevel.ASSISTANT && publicBotId) {
            dispatch(fetchOfferList({...offerTableRequest, publicBotId}));
            dispatch(fetchOfferMakeOptions({publicBotId}));
            dispatch(fetchOfferModelOptions({publicBotId}));
            dispatch(fetchOfferSourceNameOptions({publicBotId}));
        } else if (offerLevel === OfferLevel.LOCATION && publicLocationId) {
            dispatch(fetchOfferList({...offerTableRequest, publicLocationId}));
            dispatch(fetchOfferMakeOptions({publicLocationId}));
            dispatch(fetchOfferModelOptions({publicLocationId}));
            dispatch(fetchOfferSourceNameOptions({publicLocationId}));
        } else {
            throw new Error("Issue on offer table initialization");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        publicBotId,
        publicLocationId,
        offerLevel,
        pageNumber,
        searchQuery,
        makeList,
        modelList,
        offerTypeList,
        offerSourceNameList,
    ])

    const isLoading = offerListRequestStatus === RequestStatus.PENDING;
    const isResolved = offerListRequestStatus === RequestStatus.RESOLVED;

    if (offerListRequestStatus === RequestStatus.REJECTED) {
        dispatch(showErrorToastNotification({message: "Failed to load offers"}))
    }

    const handleCreateOfferClick = () => {
        dispatch(openForm({
            name: FormName.CREATE_OFFER,
            formProps: {publicBotId, publicLocationId}
        }));
    }

    const handleDownloadClick = () => {
        const headers = REQUIRED_HEADERS.concat(DISCOUNT_HEADERS).concat(FINANCE_HEADERS)

        const exampleRow = REQUIRED_EXAMPLES.concat(DISCOUNT_EXAMPLES).concat(FINANCE_EXAMPLES)

        const csvContent = [headers, exampleRow]
            .map(row => row.map(v => `"${v}"`).join(","))
            .join("\n")

        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"})
        const url = URL.createObjectURL(blob)

        const link = document.createElement("a")
        link.href = url;
        link.setAttribute("download", "offers-template.csv")
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const handleImportClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            fileInputRef.current.click();
        }
    }

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return

        if (!file.name.toLowerCase().endsWith(".csv")) {
            dispatch(showErrorToastNotification({message: "Only CSV files are allowed"}))
            return
        }

        const reader = new FileReader()

        reader.onload = (e) => {
            const text = e.target?.result as string;
            processCsv(text, file.name.toLowerCase())
        }

        reader.onerror = () => {
            dispatch(showErrorToastNotification({message: "Failed to read file"}))
        }

        reader.readAsText(file)
    }

    const processCsv = (csvText: string, fileName: string) => {
        const rows = parseCsv(csvText);

        if (rows.length < 2) {
            dispatch(showErrorToastNotification({message: "CSV file is empty"}));
            return;
        }

        const headers = rows[0];
        const dataRows = rows.slice(1);

        const headerError = validateHeaders(headers);
        if (headerError) {
            dispatch(showErrorToastNotification({message: headerError}));
            return;
        }

        const result = validateAndMapRows(
            dataRows,
            headers,
            fileName,
            publicBotId,
            publicLocationId
        );

        if (!result.valid) {
            dispatch(showErrorToastNotification({message: result.error!}));
            return;
        }

        dispatch(openPopupModal({
            name: PopupModalName.PREVIEW_OFFERS,
            popupModalProps: {
                publicBotId,
                publicLocationId,
                offersToSave: result.mapped,
                offerSourceName: fileName
            }
        }));
    }

    const tableContent = (
        <div className={styles.wrapper}>
            <div className={styles.buttonsWrapper}>
                <Button
                    title="Download template"
                    state={ButtonState.SECONDARY}
                    handleClick={handleDownloadClick}
                />
                <Button
                    title="Import CSV"
                    iconSrc="/plusIcon.svg"
                    state={ButtonState.PRIMARY}
                    handleClick={handleImportClick}
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{display: "none"}}
                    onChange={handleFileSelected}
                />
                <Button
                    title="New offer"
                    iconSrc="/plusIcon.svg"
                    state={ButtonState.PRIMARY}
                    handleClick={handleCreateOfferClick}
                />
            </div>

            <div className={styles.tableWrapper}>
                <OfferTableControls/>
                <OfferTableHeader/>
                <div className={styles.tableRowsWrapper}>
                    {isLoading && <TableLoader/>}

                    {isResolved &&
                        offerList.map((offer, index) => (
                        <OfferTableRow
                            key={`offer-table-row-${index}`}
                            index={index}
                            offer={offer}
                            isLastRow={index === offerList.length - 1}
                        />
                    ))}
                </div>
            </div>

            {isResolved &&
                <TablePagination
                    pageNumber={offerTableRequest.pageNumber}
                    totalItems={offerListTotalCount}
                    itemsPerPage={OFFERS_TABLE_ITEMS_ON_PAGE}
                    onPageChange={(newPage) =>
                        dispatch(updateOfferTableRequest({pageNumber: newPage}))
                    }
                />
            }
        </div>
    )

    if ((offerList.length && offerListRequestStatus === RequestStatus.RESOLVED) || isFiltersApplied) {
        return tableContent;
    } else if (!offerList.length && offerListRequestStatus === RequestStatus.RESOLVED) {
        return <div className={styles.emptyTableWrapper}>
            <div className={styles.titleWrapper}>
                <span className={styles.title}>You haven’t had any offers yet</span>
                <span
                    className={styles.subtitle}>Get started by creating an offer. Once created, it will appear here.</span>
            </div>
            <div className={styles.buttonsOnEmptyTableWrapper}>
                <Button
                    title="Download template"
                    state={ButtonState.SECONDARY}
                    handleClick={handleDownloadClick}
                />
                <Button
                    title="Import CSV"
                    iconSrc="/plusIcon.svg"
                    state={ButtonState.PRIMARY}
                    handleClick={handleImportClick}
                />
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    style={{display: "none"}}
                    onChange={handleFileSelected}
                />
                <Button
                    title="New offer"
                    iconSrc="/plusIcon.svg"
                    state={ButtonState.PRIMARY}
                    handleClick={handleCreateOfferClick}
                />
            </div>
        </div>
    } else {
        return <></>
    }
}
